import { ArrowLeft, Save } from 'lucide-react';
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import type { CriarSociedadeDto, Pagina, Pessoa, Sociedade } from '@social/shared';
import { PaginaShell } from '../componentes/pagina-shell';
import { Aviso, Botao } from '../componentes/ui';
import { ErroApi } from '../lib/api';
import { api } from '../lib/sessao';
import {
  capitalDerivado,
  centavosDeReais,
  hojeISO,
  montarDto,
  type RascunhoSociedade,
} from './sociedade-nova-comum';
import { CamposDaSociedade } from './sociedade-campos';

/*
  Cadastro de sociedade — a constituição.

  Herda o padrão que o cadastro de pessoa fixou (seções em `<fieldset>`, campo
  condicional que some, erro do servidor em `Aviso`), com três decisões que só
  este formulário precisa tomar:

  1. **O capital é DERIVADO, não digitado.** A regra `capital-nao-bate` do
     validador exige `soma(quotas) × valor nominal === capital`. Um campo de
     capital ao lado das quotas é convite à divergência: a pessoa preenche as
     quotas, arredonda o capital, e o laudo reprova por centavos. Aqui o número
     aparece calculado, em tempo real.

  2. **Sócio se ESCOLHE, não se digita.** Toda participação aponta para uma
     `pessoaId` que já existe — por isso a tela de pessoas veio primeiro. Sem
     ninguém cadastrado, o formulário diz o que fazer em vez de mostrar um
     seletor vazio.

  3. **Estado local, não `react-hook-form`.** O de pessoa usa RHF porque é um
     objeto plano validado por um schema. Aqui há três arrays aninhados cujo
     conteúdo se CALCULA entre si (quotas → capital), e o `useFieldArray` com
     `watch` a cada tecla ficaria mais difícil de ler do que um `useState` com
     funções nomeadas. A validação forte continua sendo do servidor, que roda
     o validador jurídico inteiro e devolve o laudo.
*/

const VAZIO: RascunhoSociedade = {
  tipo: 'LTDA',
  nomeEmpresarial: '',
  nomeFantasia: '',
  inicioAtividades: hojeISO(),
  objetoSocial: '',
  enquadramento: 'ME',
  valorNominalQuota: '1,00',
  foroComarca: '',
  foroUf: '',
  sede: {
    logradouro: '',
    numero: '',
    bairro: '',
    municipio: '',
    uf: '',
    cep: '',
  },
  atividades: [{ cnae: '', descricao: '', principal: true }],
  socios: [{ pessoaId: '', quotas: '', integralizadas: '' }],
  administradores: [
    {
      pessoaId: '',
      usoIsoladoDoNome: true,
      vedadoFiancaAvalEndosso: true,
      podeConstituirProcuradores: true,
    },
  ],
};

export function SociedadeNova(): JSX.Element {
  const navegar = useNavigate();
  const fila = useQueryClient();
  const [r, setR] = useState<RascunhoSociedade>(VAZIO);

  const pessoasQuery = useQuery({
    queryKey: ['pessoas', 'consulta'] as const,
    queryFn: () => api.get<Pagina<Pessoa>>('/pessoas?tamanho=200'),
  });
  const pessoas = pessoasQuery.data?.itens ?? [];

  const valorNominal = centavosDeReais(r.valorNominalQuota);
  const capital = capitalDerivado(
    r.socios.map((s) => Number(s.quotas)),
    valorNominal,
  );

  const salvar = useMutation({
    mutationFn: (dto: CriarSociedadeDto) => api.post<Sociedade>('/sociedades', dto),
    onSuccess: async (criada) => {
      await fila.invalidateQueries({ queryKey: ['sociedades'] });
      // Vai direto para o detalhe: quem acabou de constituir quer conferir o
      // laudo e gerar o contrato, não voltar para a lista e procurar.
      navegar(`/sociedades/${criada.id}`);
    },
  });

  const mudar = (parcial: Partial<RascunhoSociedade>) =>
    setR((atual) => ({ ...atual, ...parcial }));

  const enviar = (evento: React.FormEvent) => {
    evento.preventDefault();
    salvar.mutate(montarDto(r, valorNominal, capital));
  };

  // Sem ninguém cadastrado não há como escolher sócio — e um seletor vazio
  // com "Selecione…" é um beco sem saída. A tela diz o que fazer.
  if (!pessoasQuery.isPending && pessoas.length === 0) {
    return (
      <PaginaShell titulo="Nova sociedade">
        <Aviso>
          Cadastre ao menos uma pessoa antes: toda sociedade precisa de sócio e de
          administrador, e os dois saem do cadastro de pessoas.
        </Aviso>
        <div className="mt-3">
          <Link to="/pessoas/nova" className="text-marca-600 text-sm hover:underline">
            Cadastrar a primeira pessoa →
          </Link>
        </div>
      </PaginaShell>
    );
  }

  return (
    <PaginaShell
      titulo="Nova sociedade"
      subtitulo="Os dados que o contrato social precisa para nascer válido"
      acoes={
        <Link
          to="/sociedades"
          className="border-tinta-100 bg-campo text-tinta-900 hover:border-marca-600 hover:bg-marca-600/10 inline-flex h-11 items-center gap-2 rounded-lg border px-3 text-sm whitespace-nowrap transition-colors"
        >
          <ArrowLeft className="size-4" aria-hidden />
          Voltar
        </Link>
      }
    >
      <form onSubmit={enviar} className="flex flex-col gap-4" noValidate>
        {salvar.isError && <Aviso>{mensagemDoErro(salvar.error)}</Aviso>}

        <CamposDaSociedade
          rascunho={r}
          mudar={mudar}
          pessoas={pessoas}
          capital={capital}
          valorNominal={valorNominal}
        />

        <div className="border-tinta-100 flex items-center justify-end gap-2 border-t pt-4">
          <Link
            to="/sociedades"
            className="text-tinta-700 hover:bg-marca-600/10 inline-flex h-11 items-center rounded-lg px-4 text-sm transition-colors"
          >
            Cancelar
          </Link>
          <Botao type="submit" carregando={salvar.isPending}>
            <Save className="size-4" aria-hidden />
            Constituir sociedade
          </Botao>
        </div>
      </form>
    </PaginaShell>
  );
}

function mensagemDoErro(erro: unknown): string {
  if (erro instanceof ErroApi) return erro.message;
  return 'Não foi possível constituir a sociedade. Tente de novo.';
}

/** Reexportado para o teste medir o capital sem repetir a conta. */
export { capitalDerivado };
